import React, { useState, useEffect } from 'react';
import './CustomModal.css';
import { getSamlSettings, createSamlConnection, updateConnection, createScimConnection, refreshToken } from './services';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { useAppSelector } from '../../../../../../redux/hooks';

interface CustomModalProps {
  show: boolean;
  handleClose: () => void;
  title: React.ReactNode;
  portName: string;
  ssoDetails?: {
    connection_id?: string;
    signin_url?: string;
    sso_url?: string;
    audience_uri?: string;
    email_domain?: string;
    scim_token?: string;
    certificate_name?: string;
    scim_ep?: string;
    token_id?: string;
  };
  onSave: () => void;
}

const CustomModal: React.FC<CustomModalProps> = ({
  show,
  handleClose,
  title,
  portName,
  ssoDetails,
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState<'SSO' | 'SCIM'>('SSO');
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [initialData, setInitialData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const tenant = useAppSelector(state => state.TenantSlice.id);

  // Estados para manejar los datos de SSO
  const [signInUrl, setSignInUrl] = useState('');
  const [certificate, setCertificate] = useState<File | null>(null);
  const [certificateSelected, setCertificateSelected] = useState(false); // Estado para indicar si se seleccionó un archivo
  const [ssoUrl, setSsoUrl] = useState('');
  const [audienceUri, setAudienceUri] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);

  // Estados para manejar los datos de SCIM
  const [scimEndpoint, setScimEndpoint] = useState('');
  const [scimToken, setScimToken] = useState('');
  const [tokenId, setTokenId] = useState<string | null>(null);

  // Nuevo estado para controlar si la pestaña SCIM está deshabilitada
  const [isSCIMTabDisabled, setIsSCIMTabDisabled] = useState(false);

  useEffect(() => {
    if (show) {
      if (ssoDetails) {
        setSignInUrl(ssoDetails.signin_url || '');
        setSsoUrl(ssoDetails.sso_url || '');
        setAudienceUri(ssoDetails.audience_uri || '');
        setEmailDomain(ssoDetails.email_domain || '');
        setScimToken(ssoDetails.scim_token || '');
        setScimEndpoint(ssoDetails.scim_ep || ''); // Establecer el valor del SCIM Endpoint
        setConnectionId(ssoDetails.connection_id || null);
        setTokenId(ssoDetails.token_id || null); // Establecer el valor del token_id

        // Guardar los valores iniciales para detectar cambios
        setInitialData({
          signin_url: ssoDetails.signin_url || '',
          audience_uri: ssoDetails.audience_uri || '',
          email_domain: ssoDetails.email_domain || '',
        });
        setIsSaveDisabled(true);

        // Desbloquear la pestaña SCIM durante la edición
        setIsSCIMTabDisabled(false);
      } else {
        setIsLoading(true);
        getSamlSettings(tenant)
          .then((response) => {
            const data = response.data;
            setSsoUrl(data.callbackUrl);
            setAudienceUri(data.audienceUri);
            setIsLoading(false);
          })
          .catch((error) => {
            console.error('Error fetching SAML settings:', error);
            setIsLoading(false);
          });

        // Bloquear la pestaña SCIM durante la creación
        setIsSCIMTabDisabled(true);
      }
    }
  }, [show, ssoDetails]);

  // Función para verificar si se han cumplido los requisitos para habilitar el botón Save
  useEffect(() => {
    // Verificar si es un update mediante connectionId (si existe connectionId, estamos en modo actualización)
    const isUpdate = Boolean(connectionId);

    // Si es update, el certificado es requerido solo si no existe un certificate_name y no se ha subido un archivo nuevo
    const isCertificateRequired = !isUpdate || (!certificate && !ssoDetails?.certificate_name);

    // Verificar si todos los campos obligatorios están llenos (incluyendo el certificado si es requerido)
    const areRequiredFieldsFilled =
      signInUrl.trim() !== '' &&
      audienceUri.trim() !== '' &&
      emailDomain.trim() !== '' &&
      (!isCertificateRequired || (certificate && certificateSelected)); // Si el certificado es requerido, validamos que esté seleccionado o que exista un certificate_name.

    // Verificar si se han realizado cambios en los campos o si se seleccionó un nuevo certificado
    const hasChanges =
      signInUrl !== initialData?.signin_url ||
      audienceUri !== initialData?.audience_uri ||
      emailDomain !== initialData?.email_domain ||
      (certificate && certificateSelected);

    // Si es una actualización y no hay cambios en los datos, deshabilitar el botón
    const isUpdateWithNoChanges = isUpdate && !hasChanges;

    // Actualizar el estado del botón Save dependiendo de los campos llenos, si hay cambios o si es un update sin cambios
    setIsSaveDisabled(!(areRequiredFieldsFilled && (!isUpdateWithNoChanges)));
  }, [signInUrl, audienceUri, emailDomain, certificate, certificateSelected, initialData, connectionId, ssoDetails]);

  // Función para guardar o actualizar la configuración
  const saveSSOConfiguration = () => {
    // Validación para no permitir el dominio "transitxchange.cloud"
    if (emailDomain === 'transitxchange.cloud') {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Domain',
        text: 'The domain "transitxchange.cloud" cannot be saved or edited.',
      });
      return; // Interrumpe el guardado
    }

    const formData = new FormData();
    formData.append('signinUrl', signInUrl);
    if (certificate) {
      formData.append('file', certificate);
    }
    formData.append('audienceUri', audienceUri);
    formData.append('emailDomain', emailDomain);
    formData.append('tenantId', tenant.toString());

    if (connectionId) {
      // Actualización de una conexión existente
      updateConnection(connectionId, formData)
        .then(() => {
          setInitialData({
            signin_url: signInUrl,
            audience_uri: audienceUri,
            email_domain: emailDomain,
          });
          setIsSaveDisabled(true);
          showSCIMAlert('updated');
        })
        .catch((error) => {
          console.error('Error updating SSO configuration:', error);
          showSCIMAlert('error');
        });
    } else {
      // Creación de una nueva conexión
      createSamlConnection(formData, tenant)
        .then((response) => {
          setConnectionId(response.data.connection_id);
          setScimEndpoint(response.data.scim_ep);
          setInitialData({
            signin_url: signInUrl,
            audience_uri: audienceUri,
            sso_url: ssoUrl,
            email_domain: emailDomain,
          });
          setIsSaveDisabled(true);
          showSCIMAlert('saved');
        })
        .catch((error) => {
          console.error('Error creating SSO configuration:', error);
          showSCIMAlert('error');
        });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCertificate(e.target.files[0]);
      setCertificateSelected(true);
    }
  };

  // Función para mostrar alerta y continuar con SCIM
  const showSCIMAlert = (action: 'saved' | 'updated' | 'error') => {
    let message = '';
    let iconType: SweetAlertIcon = 'success'; // Definir iconType con un tipo específico
    let confirmButtonText = 'No, close';
    let cancelButtonText = 'Yes, continue';
    let extraText = '';

    if (action === 'saved') {
      message = 'SSO Configuration saved';
    } else if (action === 'updated') {
      message = 'SSO Configuration updated';
    } else if (action === 'error') {
      message = 'Error creating SSO configuration';
      iconType = 'error';
      confirmButtonText = 'Close';
      cancelButtonText = '';
      extraText = 'Please contact support for assistance.'; // Leyenda adicional para el caso de error
    }

    Swal.fire({
      title: message,
      html: extraText, // Utilizamos `html` para mostrar el texto adicional debajo del mensaje principal
      text: action !== 'error' ? 'Do you want to continue configuring SCIM?' : '',
      icon: iconType, // Usar el tipo específico de SweetAlertIcon
      showCancelButton: action !== 'error',
      confirmButtonText: confirmButtonText,
      cancelButtonText: cancelButtonText,
      confirmButtonColor: action === 'error' ? '#ff8c00' : '#ffc107',
      cancelButtonColor: '#28a745',
    }).then((result) => {
      if (result.isDismissed && action !== 'error') {
        setIsSCIMTabDisabled(false);
        setActiveTab('SCIM');
      } else if (action !== 'error') {
        handleClose();
        onSave();
      }
    });
  };

  // Función para generar o actualizar el SCIM Token
  const handleSCIMToken = () => {
    if (tokenId) {
      // Si existe un token_id, refrescamos el token
      setIsLoading(true);
      refreshToken(connectionId)
        .then((response) => {
          setScimToken(response.data.token);
          Swal.fire({
            title: 'SCIM Token Updated',
            text: 'The SCIM token was updated successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error updating SCIM token:', error);
          Swal.fire({
            title: 'Error',
            text: 'There was an error updating the SCIM token.',
            icon: 'error',
            timer: 2000,
            showConfirmButton: false,
          });
          setIsLoading(false);
        });
    } else {
      // Si no existe un token_id, generamos uno nuevo
      if (connectionId) {
        setIsLoading(true);
        createScimConnection(connectionId)
          .then((response) => {
            setScimToken(response.data.token);
            setTokenId(response.data.token_id);
            Swal.fire({
              title: 'SCIM Token Generated',
              text: 'The SCIM token was generated successfully.',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false,
            });
            setIsLoading(false);
          })
          .catch((error) => {
            console.error('Error generating SCIM token:', error);
            Swal.fire({
              title: 'Error',
              text: 'There was an error generating the SCIM token.',
              icon: 'error',
              timer: 2000,
              showConfirmButton: false,
            });
            setIsLoading(false);
          });
      } else {
        console.error('Connection ID is required to generate SCIM token');
      }
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="custom-modal-overlay">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <i className="fas fa-cog fa-spin"></i>
          </div>
        </div>
      )}

      <div className="custom-modal-connectivity">
        <div className="custom-modal-header">
          <h4>{title}</h4>
          <div className="custom-modal-subtitle">
          </div>
          <button onClick={() => { handleClose(); onSave(); }} className="close-button">&times;</button>
        </div>

        <div className="tab-header">
          <button onClick={() => setActiveTab('SSO')} className={`tab-button ${activeTab === 'SSO' ? 'active' : ''}`}>SSO</button>
          <button
            onClick={() => setActiveTab('SCIM')}
            className={`tab-button ${activeTab === 'SCIM' ? 'active' : ''}`}
            disabled={isSCIMTabDisabled} // Deshabilitar el botón si la pestaña está bloqueada
          >
            SCIM
          </button>
        </div>

        {activeTab === 'SSO' && (
          <div className="tab-content">
            <div className="form-group" style={{ position: 'relative' }}>
              <label htmlFor="ssoUrl">
                Single Sign-On URL:
                <span className="tooltip-icon" data-tooltip="This URL is provided by us, and you should place it where instructed by your Identity Provider (Okta, Azure AD, Google Identity Platform)">
                  <i className="fas fa-info-circle"></i>
                </span>
              </label>
              <input
                type="text"
                id="ssoUrl"
                value={ssoUrl}
                className="form-control"
                style={{ flexGrow: 1, paddingRight: '40px' }}
                readOnly
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(ssoUrl);
                  Swal.fire({
                    title: 'URL Copied',
                    text: 'The Single Sign-On URL has been copied to your clipboard.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                  });
                }}
                className="copy-token-button"
                style={{
                  position: 'absolute',
                  top: '68%',
                  right: '0px',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#007bff',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-copy" style={{ fontSize: '2rem' }}></i>
              </button>
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label htmlFor="audienceUri">
                Audience URI: <span style={{ color: 'red' }}>*</span>
                <span className="tooltip-icon" data-tooltip="This parameter is provided by us, and you should place it as instructed by your Identity Provider (Okta, Azure AD, Google Identity Platform)">
                  <i className="fas fa-info-circle"></i>
                </span>
              </label>
              <input
                type="text"
                id="audienceUri"
                value={audienceUri}
                className="form-control"
                style={{ flexGrow: 1, paddingRight: '40px' }}
                readOnly
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(audienceUri);
                  Swal.fire({
                    title: 'URI Copied',
                    text: 'The Audience URI has been copied to your clipboard.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                  });
                }}
                className="copy-token-button"
                style={{
                  position: 'absolute',
                  top: '68%',
                  right: '0px',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#007bff',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-copy" style={{ fontSize: '2rem' }}></i>
              </button>
            </div>

            <div className="form-group">
              <label htmlFor="signInUrl">
                Sign-On URL: <span style={{ color: 'red' }}>*</span>
                <span className="tooltip-icon" data-tooltip="This URL is provided by your Identity Provider (Okta, Azure AD, Google Identity Platform)">
                  <i className="fas fa-info-circle"></i>
                </span>
              </label>
              <input
                id="signInUrl"
                type="text"
                placeholder="https://example.com/login"
                value={signInUrl}
                onChange={(e) => setSignInUrl(e.target.value)}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="certificate">
                X509 Signing Certificate:
                <span
                  className="tooltip-icon"
                  data-tooltip="Upload the X509 signing certificate file that you obtained from the Identity Provider (Okta, Azure AD, Google Identity Platform)"
                >
                  <i className="fas fa-info-circle"></i>
                </span>
              </label>

              <div
                className="custom-file-input-container"
                style={{
                  position: 'relative',
                  border: '1px dashed #ccc',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Asegura que solo el contenedor maneje el clic
                  const fileInput = document.getElementById('certificate');
                  if (fileInput) {
                    (fileInput as HTMLInputElement).click();
                  }
                }}
              >
                <input
                  type="file"
                  id="certificate"
                  className="custom-file-input"
                  onChange={handleFileChange}
                  style={{ display: 'none' }} // Oculta el input
                />
                {certificate || ssoDetails?.certificate_name ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <i className="fas fa-file-alt" style={{ marginRight: '8px' }}></i>
                      <span style={{ fontWeight: 'bold' }}>
                        {certificate ? certificate.name : ssoDetails?.certificate_name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // Evita que el evento de clic llegue al contenedor
                        setCertificate(null);
                        setCertificateSelected(false);
                      }}
                      className="clear-file-button"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'red',
                        fontSize: '20px',
                        cursor: 'pointer',
                      }}
                    >
                      <i className="fas fa-trash" style={{ fontSize: '1.5rem' }}></i>
                    </button>
                  </div>
                ) : (
                  <div>
                    <p>Drop your certificate file here or click to upload</p>
                    <p style={{ fontSize: '12px', color: '#777' }}>
                      Supports PEM, CER, CRT, and DER formats
                    </p>
                  </div>
                )}
              </div>

              <p
                className="certificate-info"
                style={{ marginTop: '10px', fontSize: '12px', color: '#333' }}
              >
                SAMLP server public key encoded in PEM or CER format
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="emailDomain">
                Email Domain: <span style={{ color: 'red' }}>*</span>
                <span className="tooltip-icon" data-tooltip="Email domain for users who will use this connection to perform SSO. All emails with this domain will be automatically redirected to the provided Identity Provider (Okta, Azure AD, Google Identity Platform)">
                  <i className="fas fa-info-circle"></i>
                </span>
              </label>
              <input
                id="emailDomain"
                type="text"
                placeholder="example.com"
                value={emailDomain}
                onChange={(e) => setEmailDomain(e.target.value)}
                className="form-control"
              />
            </div>

            <div className="custom-modal-footer">
              <div className="btn-actions">
                <button
                  onClick={() => { handleClose(); onSave(); }}
                  className="btn-cancel btn border border-pink text-pink f-parrafo"
                  style={{ padding: '8px 16px' }}
                >
                  Close
                </button>
                <button
                  onClick={saveSSOConfiguration}
                  className="btn-light-marino f-parrafo"
                  disabled={isSaveDisabled}
                >
                  {connectionId ? 'Update SSO Configuration' : 'Save SSO Configuration'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'SCIM' && (
          <div className="tab-content">
            <div className="form-group" style={{ position: 'relative' }}>
              <label htmlFor="scimEndpoint">
                SCIM URL:
                <span className="tooltip-icon" data-tooltip="Provided for enabling SCIM user provisioning in your Identity Provider (Okta, Azure AD, Google Identity Platform).">
                  <i className="fas fa-info-circle"></i>
                </span>
              </label>
              <input
                id="scimEndpoint"
                type="text"
                value={scimEndpoint}
                className="form-control"
                readOnly
                style={{ flexGrow: 1, paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(scimEndpoint);
                  Swal.fire({
                    title: 'SCIM URL Copied',
                    text: 'The SCIM URL has been copied to your clipboard.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                  });
                }}
                className="copy-token-button"
                style={{
                  position: 'absolute',
                  right: '0px',
                  top: '68%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#007bff',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-copy" style={{ fontSize: '2rem' }}></i>
              </button>
            </div>
            <div className="scim-token-container" style={{ position: 'relative' }}>
              <label htmlFor="scimToken">
                Token:
                <span className="tooltip-icon" data-tooltip="SCIM Token Provided by CTX to configure on your Identity Provider (Okta, Azure AD, Google Identity Platform) to authenticate with CTX; set the authentication type in the HTTP Header">
                  <i className="fas fa-info-circle"></i>
                </span>
              </label>
              <input
                type={showToken ? "text" : "password"} // Para alternar entre mostrar y ocultar
                id="scimToken"
                value={scimToken}
                className="form-control"
                readOnly
                style={{ flexGrow: 1, paddingRight: '70px' }}
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)} // Lógica para mostrar/ocultar el token
                className="toggle-visibility-button"
                style={{
                  position: 'absolute',
                  right: '40px',
                  top: '68%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#007bff',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                <i className={showToken ? "fas fa-eye-slash" : "fas fa-eye"} style={{ fontSize: '1.5rem' }}></i>
              </button>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(scimToken);
                  Swal.fire({
                    title: 'Token Copied',
                    text: 'The SCIM token has been copied to your clipboard.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                  });
                }}
                className="copy-token-button"
                style={{
                  position: 'absolute',
                  right: '0px',
                  top: '68%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#007bff',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-copy" style={{ fontSize: '2rem' }}></i>
              </button>
            </div>
            <div className="custom-modal-footer">
              <div className="info-container">
                <p className="info-text">
                  <i className="info-icon fas fa-exclamation-circle"></i>
                  <span>Important: Save this token securely. It will not be shown again.</span>
                </p>
              </div>
              <div className="btn-actions">
                <button
                  onClick={() => { handleClose(); onSave(); }}
                  className="btn-cancel btn border border-pink text-pink f-parrafo"
                  style={{ padding: '8px 16px' }}
                >
                  Close
                </button>
                <button onClick={handleSCIMToken} className="btn-light-marino f-parrafo">
                  {tokenId ? 'Update Token' : 'Generate Token'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomModal;
